import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { FlatTab, FlatTabItem } from "@/components/FlatTab";
import { ROUTES } from "@/routes/paths";
import { PageTitle } from "@/components/PageTitle";
import { FeatureDialog } from "@/components/FeatureDialog";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { RouteId } from "@/routes/getRoute";
import { FEATURE_PROTECTED_ROUTES } from "@/routes/protectedRoutes";
import { RiseAltLogo } from "@/assets/svg/RiseAltLogo";
import { useOrg } from "@/hooks/useOrg";
import { CURRENT_COHORT_YEAR } from "@/api/queries/currentCohortYear";
import { PredictiveModelWrapper } from "./PredictiveModelWrapper";
import { PredictiveModelType } from "./types";

export const PredictiveModels = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { navToRoute } = useProtectedNavigation();
  const { hasOrgFeature } = useOrgPermissions();
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);

  const { org, loading: orgIdLoading, error: orgIdError } = useOrg();

  const {
    data: cohortData,
    loading: cohortLoading,
    error: cohortError,
  } = useQuery(CURRENT_COHORT_YEAR);

  const cohortYear = cohortData?.currentCohortYear;

  const ROUTE_IDS = {
    APPLY_INDEX: "applyIndex" as RouteId,
    COMMIT_INDEX: "commitIndex" as RouteId,
    MELT_INDEX: "meltIndex" as RouteId,
    YIELD_INDEX: "yieldIndex" as RouteId,
  };

  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes(ROUTES.ANALYTICS.APPLY_INDEX))
      return ROUTES.ANALYTICS.APPLY_INDEX;
    if (path.includes(ROUTES.ANALYTICS.MELT_INDEX))
      return ROUTES.ANALYTICS.MELT_INDEX;
    if (path.includes(ROUTES.ANALYTICS.YIELD_INDEX))
      return ROUTES.ANALYTICS.YIELD_INDEX;
    if (path.includes(ROUTES.ANALYTICS.RETAIN_INDEX))
      return ROUTES.ANALYTICS.RETAIN_INDEX;
    return ROUTES.ANALYTICS.COMMIT_INDEX;
  };

  const activeTab = getActiveTab();

  const hasRise = hasOrgFeature(PartnerFeatureEnum.Rise);

  useEffect(() => {
    const isOnBasePredictiveModelsRoute = location.pathname.endsWith(
      `/${ROUTES.ANALYTICS.PREDICTIVE_MODELS}`,
    );

    if (isOnBasePredictiveModelsRoute) {
      if (hasRise) {
        // For RISE users, use seasonal routing
        const now = new Date();
        const month = now.getMonth() + 1;

        // Aug 1 - Dec 31: Apply Index (months 8-12)
        // Jan 1 - July 31: Commit Index (months 1-7)
        const isAdmissionsSeason = month >= 8;

        if (isAdmissionsSeason) {
          navToRoute(ROUTE_IDS.APPLY_INDEX);
        } else {
          navToRoute(ROUTE_IDS.COMMIT_INDEX);
        }
      } else {
        navToRoute(ROUTE_IDS.COMMIT_INDEX);
      }
    }
  }, [
    hasRise,
    location.pathname,
    navToRoute,
    ROUTE_IDS.APPLY_INDEX,
    ROUTE_IDS.COMMIT_INDEX,
  ]);

  const handleTabClick = (routeId: RouteId) => {
    const requiredFeature = FEATURE_PROTECTED_ROUTES[routeId];

    if (requiredFeature === PartnerFeatureEnum.Rise && !hasRise) {
      setShowFeatureDialog(true);
      return;
    }

    navToRoute(routeId);
  };

  const tabItems: FlatTabItem[] = [
    {
      label: t("predictiveModels.tabs.applyIndex"),
      active: activeTab === ROUTES.ANALYTICS.APPLY_INDEX,
      onClick: () => handleTabClick("applyIndex"),
    },
    {
      label: t("predictiveModels.tabs.commitIndex"),
      active: activeTab === ROUTES.ANALYTICS.COMMIT_INDEX,
      onClick: () => handleTabClick("commitIndex"),
    },
    {
      label: t("predictiveModels.tabs.meltIndex"),
      active: activeTab === ROUTES.ANALYTICS.MELT_INDEX,
      onClick: () => handleTabClick("meltIndex"),
    },
    {
      label: t("predictiveModels.tabs.yieldIndex"),
      active: activeTab === ROUTES.ANALYTICS.YIELD_INDEX,
      onClick: () => handleTabClick("yieldIndex"),
    },
    // Retain Index tab stays commented out as requested
    // {
    //   label: t("predictiveModels.tabs.retainIndex"),
    //   active: activeTab === ROUTES.ANALYTICS.RETAIN_INDEX,
    //   onClick: () => handleTabClick('retainIndex'),
    // },
  ];

  const getModelTypeForActiveTab = (): PredictiveModelType => {
    if (!hasRise) {
      return "Commit";
    }

    switch (activeTab) {
      case ROUTES.ANALYTICS.APPLY_INDEX:
        return "Apply";
      case ROUTES.ANALYTICS.MELT_INDEX:
        return "Melt";
      case ROUTES.ANALYTICS.YIELD_INDEX:
        return "Yield";
      case ROUTES.ANALYTICS.COMMIT_INDEX:
      default:
        return "Commit";
    }
  };

  const renderPredictiveModel = () => {
    const modelType = getModelTypeForActiveTab();

    return (
      <PredictiveModelWrapper
        orgId={org?.id}
        orgIdLoading={orgIdLoading}
        orgIdError={orgIdError}
        cohortYear={cohortYear}
        cohortLoading={cohortLoading}
        cohortError={cohortError}
        modelType={modelType}
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <PageTitle
        title={t("predictiveModels.title")}
        logoComponent={
          hasRise ? <RiseAltLogo className="h-[24px] w-[50px]" /> : undefined
        }
      />

      <div className="ml-6">
        <FlatTab items={tabItems} />
      </div>

      <div className="w-full border-b border-border"></div>

      <div className="p-6">{renderPredictiveModel()}</div>

      <FeatureDialog
        isOpen={showFeatureDialog}
        onOpenChange={setShowFeatureDialog}
        type="feature"
        feature={PartnerFeatureEnum.Rise}
      />
    </div>
  );
};
